import { useState, useEffect } from "react";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
} from "@mui/material";
import { MoreHorizontal, ArrowUpDown, CheckSquare, Archive, Settings, Check, ChevronRight } from "lucide-react";
import { colors } from "./Pipeline.styles";

const PipelineColumnMenu = ({ columnId, sortOption = "date-newest", onAction }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState(null);
  const [selectedSort, setSelectedSort] = useState(sortOption);
  const open = Boolean(anchorEl);
  const sortMenuOpen = Boolean(sortMenuAnchorEl);

  // Sync selectedSort with sortOption prop when it changes
  useEffect(() => {
    setSelectedSort(sortOption);
  }, [sortOption]);

  const handleClick = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e) => {
    e?.stopPropagation();
    setAnchorEl(null);
    setSortMenuAnchorEl(null);
  };

  const handleSortMenuOpen = (e) => {
    e.stopPropagation();
    setSortMenuAnchorEl(e.currentTarget);
  };

  const handleSortMenuClose = (e) => {
    e?.stopPropagation();
    setSortMenuAnchorEl(null);
  };

  const handleSortSelect = (e, sortOption) => {
    e.stopPropagation();
    setSelectedSort(sortOption);
    handleSortMenuClose(e);
    handleClose(e);
    onAction?.("sort-by", columnId, sortOption);
  };

  const handleMenuAction = (e, action) => {
    e.stopPropagation();
    handleClose(e);
    onAction?.(action, columnId);
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        sx={{
          width: 24,
          height: 24,
          color: colors.textMuted,
          "&:hover": {
            bgcolor: colors.borderLight,
            color: colors.textPrimary,
          },
        }}
      >
        <MoreHorizontal size={18} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 200,
            maxHeight: 400,
            overflowY: "auto",
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            borderRadius: 2.5,
            border: `1px solid ${colors.borderLight}`,
            "& .MuiMenuItem-root": {
              fontSize: 13,
              fontWeight: 500,
              py: 1.25,
              px: 2,
              color: colors.textPrimary,
              borderRadius: 1.5,
              mx: 0.75,
              my: 0.25,
              "&:hover": {
                bgcolor: "#F8F7FF",
              },
            },
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              bgcolor: "#FFFFFF",
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: colors.primary,
              borderRadius: "3px",
              "&:hover": {
                bgcolor: colors.primaryDark,
              },
            },
          },
        }}
      >
        <MenuItem onClick={handleSortMenuOpen}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <ArrowUpDown size={18} color={colors.textSecondary} />
          </ListItemIcon>
          <ListItemText
            primary="Sort By"
            primaryTypographyProps={{
              sx: { fontSize: 13, fontWeight: 500 }
            }}
          />
          <ChevronRight size={16} color={colors.textMuted} />
        </MenuItem>

        <MenuItem onClick={(e) => handleMenuAction(e, "bulk-action")}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <CheckSquare size={18} color={colors.textSecondary} />
          </ListItemIcon>
          <ListItemText
            primary="Bulk Action"
            primaryTypographyProps={{
              sx: { fontSize: 13, fontWeight: 500 }
            }}
          />
        </MenuItem>

        <MenuItem onClick={(e) => handleMenuAction(e, "archive-leads")}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Archive size={18} color={colors.textSecondary} />
          </ListItemIcon>
          <ListItemText
            primary="Archive Leads"
            primaryTypographyProps={{
              sx: { fontSize: 13, fontWeight: 500 }
            }}
          />
        </MenuItem>

        <Divider sx={{ my: 0.5, mx: 0.75 }} />

        <MenuItem onClick={(e) => handleMenuAction(e, "column-settings")}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Settings size={18} color={colors.textSecondary} />
          </ListItemIcon>
          <ListItemText
            primary="Column Settings"
            primaryTypographyProps={{
              sx: { fontSize: 13, fontWeight: 500 }
            }}
          />
        </MenuItem>
      </Menu>

      {/* Sort By Submenu */}
      <Menu
        anchorEl={sortMenuAnchorEl}
        open={sortMenuOpen}
        onClose={handleSortMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            ml: 0.5,
            minWidth: 220,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            borderRadius: 2.5,
            border: `1px solid ${colors.borderLight}`,
            "& .MuiMenuItem-root": {
              fontSize: 13,
              fontWeight: 500,
              py: 1.25,
              px: 2,
              color: colors.textPrimary,
              borderRadius: 1.5,
              mx: 0.75,
              my: 0.25,
              "&:hover": {
                bgcolor: "#F8F7FF",
              },
            },
          },
        }}
      >
        <MenuItem onClick={(e) => handleSortSelect(e, "date-newest")}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            {selectedSort === "date-newest" && <Check size={18} color="#8B5CF6" />}
          </ListItemIcon>
          <ListItemText
            primary="Created Date (Newest)"
            primaryTypographyProps={{
              sx: {
                fontSize: 13,
                fontWeight: selectedSort === "date-newest" ? 600 : 500,
                color: selectedSort === "date-newest" ? "#8B5CF6" : colors.textPrimary
              }
            }}
          />
        </MenuItem>

        <MenuItem onClick={(e) => handleSortSelect(e, "date-oldest")}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            {selectedSort === "date-oldest" && <Check size={18} color="#8B5CF6" />}
          </ListItemIcon>
          <ListItemText
            primary="Created Date (Oldest)"
            primaryTypographyProps={{
              sx: {
                fontSize: 13,
                fontWeight: selectedSort === "date-oldest" ? 600 : 500,
                color: selectedSort === "date-oldest" ? "#8B5CF6" : colors.textPrimary
              }
            }}
          />
        </MenuItem>

        <MenuItem onClick={(e) => handleSortSelect(e, "confidence-high")}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            {selectedSort === "confidence-high" && <Check size={18} color="#8B5CF6" />}
          </ListItemIcon>
          <ListItemText
            primary="Confidence (High to Low)"
            primaryTypographyProps={{
              sx: {
                fontSize: 13,
                fontWeight: selectedSort === "confidence-high" ? 600 : 500,
                color: selectedSort === "confidence-high" ? "#8B5CF6" : colors.textPrimary
              }
            }}
          />
        </MenuItem>

        <MenuItem onClick={(e) => handleSortSelect(e, "confidence-low")}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            {selectedSort === "confidence-low" && <Check size={18} color="#8B5CF6" />}
          </ListItemIcon>
          <ListItemText
            primary="Confidence (Low to High)"
            primaryTypographyProps={{
              sx: {
                fontSize: 13,
                fontWeight: selectedSort === "confidence-low" ? 600 : 500,
                color: selectedSort === "confidence-low" ? "#8B5CF6" : colors.textPrimary
              }
            }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default PipelineColumnMenu;
